import React, { useState, useEffect } from "react";
import { Plus, Search, FileText, Download, Eye } from "lucide-react";
import { pdf } from "@react-pdf/renderer"; // ← direct download
import { WorkOrderPDFDocument } from "./WorkOrderPDFDocument"; // ← same doc as preview modal
import { ContractorApi } from "api/contractors/service";
import { differenceInDays } from "date-fns";

// Utility for classes
const cn = (...classes) => classes.filter(Boolean).join(" ");

const Card = ({ className = "", children }) => (
  <div
    className={`rounded-lg border bg-white text-card-foreground shadow-sm ${className}`}
  >
    {children}
  </div>
);

const CardContent = ({ className = "", children }) => (
  <div className={`p-6 ${className}`}>{children}</div>
);

const Button = ({
  variant = "default",
  size = "default",
  className = "",
  children,
  ...props
}) => {
  const baseStyles =
    "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";
  const variants = {
    default: "bg-sm-primary text-white shadow hover:bg-sm-primary/90",
    destructive:
      "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
    outline:
      "border border-input bg-transparent shadow-sm hover:bg-sm-accent hover:text-sm-accent-foreground",
    secondary:
      "bg-sm-secondary text-sm-secondary-foreground shadow-sm hover:bg-sm-secondary/80",
    ghost: "hover:bg-sm-accent hover:text-sm-accent-foreground",
    link: "text-sm-primary underline-offset-4 hover:underline",
  };
  const sizes = {
    default: "h-9 px-4 py-2",
    sm: "h-8 rounded-md px-3 text-xs",
    lg: "h-10 rounded-md px-8",
    icon: "h-9 w-9",
  };
  return (
    <button
      className={cn(baseStyles, variants[variant], sizes[size], className)}
      {...props}
    >
      {children}
    </button>
  );
};

const Input = ({ className = "", ...props }) => (
  <input
    className={`flex h-9 w-full rounded-md border border-input bg-transparent py-1 pl-9 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
);

const Badge = ({ variant = "default", className = "", children }) => {
  const baseStyles =
    "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-normal transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";
  const variants = {
    default:
      "border-transparent bg-sm-primary text-sm-primary-foreground hover:bg-sm-primary/80",
    secondary:
      "border-transparent bg-sm-secondary/10 text-sm-secondary hover:bg-sm-secondary/20",
    outline: "text-foreground",
  };
  const finalClass = className.includes("bg-")
    ? `${baseStyles} ${className}`
    : cn(baseStyles, variants[variant], className);
  return <div className={finalClass}>{children}</div>;
};

export function ContractorWorkOrdersTab({
  workOrders,
  contractors,
  contractor, // ← ADD THIS PROP in ContractorProfile.jsx too (pass contractor={contractor})
  selectedContractorId,
  onOpenCreateWorkOrder,
  onViewWorkOrder,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // PDF download state
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [organization, setOrganization] = useState(null);

  // Load org from localStorage (same as WorkOrderDetailView)
  useEffect(() => {
    try {
      const org = localStorage.getItem("organization");
      if (org) setOrganization(JSON.parse(org));
    } catch (err) {
      console.error("Error parsing organization:", err);
    }
  }, []);

  const parseDateSafe = (value) => {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const getDerivedWorkOrderStatus = (wo) => {
    const start = parseDateSafe(wo?.startDate);
    const end = parseDateSafe(wo?.endDate);

    // If we don't have dates, keep whatever the API/status says.
    if (!start && !end) return wo?.status ?? "";

    const now = new Date();
    const todayMidnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    ).getTime();

    if (start) {
      const startMidnight = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate(),
      ).getTime();
      if (todayMidnight < startMidnight) return "Non Active";
    }

    if (end) {
      const endOfDay = new Date(
        end.getFullYear(),
        end.getMonth(),
        end.getDate(),
        23,
        59,
        59,
        999,
      ).getTime();
      if (todayMidnight > endOfDay) return "Non Active";
    }

    return "Active";
  };

  const filteredWorkOrders = workOrders.filter((wo) => {
    if (
      searchQuery &&
      !wo.id.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !wo.projectName.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }
    if (statusFilter !== "all") {
      const normalizedFilter = statusFilter.toLowerCase();
      // For Active/Non Active, derive status based on today's date vs work order dates (only in this tab).
      if (normalizedFilter === "active" || normalizedFilter === "non active") {
        const derived = getDerivedWorkOrderStatus(wo).toLowerCase();
        if (derived !== normalizedFilter) return false;
      } else if ((wo.status ?? "").toLowerCase() !== normalizedFilter) {
        return false;
      }
    }
    return true;
  });

  const formatCurrency = (amount) => {
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
    return `₹${amount.toLocaleString()}`;
  };

  const getStatusBadgeClass = (status) => {
    const normalized = (status ?? "").toString().toLowerCase();
    switch (normalized) {
      case "active":
        return "bg-green-500/10 text-green-600 border-green-500/30";
      case "non active":
      case "non_active":
      case "inactive":
        return "bg-gray-500/10 text-gray-700 border-gray-500/30";
      case "completed":
        return "bg-sm-secondary/10 text-sm-secondary border-secondary/30";
      case "pending":
        return "bg-sm-accent/10 text-sm-accent border-sm-accent/30";
      case "on hold":
        return "bg-muted text-muted-foreground border-border";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Fetch full details then directly download PDF
  const handleDownloadWorkOrder = async (workOrder) => {
    if (!workOrder?.workorder_id) {
      // fallback: download with whatever data we have
      try {
        const docElement = React.createElement(WorkOrderPDFDocument, {
          details: workOrder,
          contractor: contractor,
          organization: organization,
        });
        const blob = await pdf(docElement).toBlob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `WorkOrder_${workOrder.id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Error generating PDF:", err);
      }
      return;
    }
    try {
      setIsLoadingDetails(true);
      const response = await ContractorApi.getWorkOrderDetail(
        workOrder.workorder_id,
      );
      if (response.status) {
        const apiData = response.data;
        const versionCount = apiData.version_timeline?.length || 0;
        const startDate =
          apiData.version_timeline?.[versionCount - 1]?.start_date;
        const endDate = apiData.version_timeline?.[0]?.end_date;
        const duration = differenceInDays(
          new Date(endDate),
          new Date(startDate),
        );

        const mappedDetails = {
          ...workOrder,
          id: apiData.id,
          status: apiData.status,
          projectName: apiData.site_name,
          contractType: apiData.contract_type,
          paymentStructure: apiData.payment_structure,
          startDate,
          endDate,
          duration,
          value: apiData.financial_summary?.total_value || 0,
          paidAmount: apiData.financial_summary?.total_paid || 0,
          approvalStatus: apiData.status,
          scopeItems: (apiData.scope_of_work || []).map((item) => ({
            id: item.id,
            item: item.item_name,
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unitCost: item.unit_cost,
            totalCost: item.total_price,
          })),
          milestones: (apiData.milestones || []).map((m) => ({
            id: m.id,
            name: m.name,
            description: m.description,
            percentage:
              (m.amount / apiData.financial_summary?.total_value) * 100,
            value: m.amount,
            status:
              m.paid_so_far >= m.amount
                ? "Paid"
                : m.paid_so_far > 0
                  ? "In Progress"
                  : "Pending",
          })),
          monthlyBills: apiData.monthly_bills,
          versions: (apiData.version_timeline || []).map((v) => ({
            version: v.version_number,
            updatedOn: v.created_on,
            totalValue: v.total_value,
            updatedBy: v.updated_by,
            scopeItems: (v.scope_items || []).map((item) => ({
              id: item.id,
              item: item.item_name,
              description: item.description,
              totalCost: item.total_price,
            })),
            milestones: (v.milestones || []).map((m) => ({
              id: m.id,
              name: m.name,
              description: m.description,
              percentage: (m.amount / v.total_value) * 100,
            })),
            terms: v.terms_and_conditions || "",
            acknowledgmentDate: v.created_on,
          })),
          terms:
            apiData.version_timeline?.[0]?.terms_and_conditions ||
            "Standard contract terms apply.",
          acknowledgmentDate: apiData.date_of_issue,
          attachments: [],
          paymentTerms: apiData.version_timeline?.[0]?.payment_terms || "",
          retentionPercent: 0,
          scopeNotes: "",
        };

        // Directly download without showing preview modal
        const docElement = React.createElement(WorkOrderPDFDocument, {
          details: mappedDetails,
          contractor: contractor,
          organization: organization,
        });
        const blob = await pdf(docElement).toBlob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `WorkOrder_${mappedDetails.id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Error fetching work order details for PDF:", err);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters Row */}
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-muted-foreground">
          {filteredWorkOrders.length} work orders
        </span>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search work orders..."
              className="h-9 text-sm w-56 border-border/60"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="w-[120px] h-9 text-sm border border-input bg-background rounded-md px-3 py-1 shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="non active">Non Active</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="on hold">On Hold</option>
          </select>
          <Button
            className="bg-sm-primary hover:bg-sm-primary/90 text-sm-primary-foreground h-9 text-sm gap-2"
            onClick={() => onOpenCreateWorkOrder?.(selectedContractorId)}
          >
            <Plus className="h-4 w-4" />
            Create Work Order
          </Button>
        </div>
      </div>

      {/* Work Orders Table */}
      <Card className="border-border/60 shadow-none">
        <CardContent className="p-0">
          <table className="w-full caption-bottom text-sm">
            <thead className="[&_tr]:border-b">
              <tr className="border-b transition-colors hover:bg-transparent border-border/60">
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-xs">
                  Work Order ID
                </th>
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-xs">
                  Project Name
                </th>
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-xs">
                  Start Date
                </th>
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-xs">
                  End Date
                </th>
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-xs">
                  Payment Structure
                </th>
                <th className="h-10 px-2 align-middle font-medium text-muted-foreground text-xs text-right">
                  Value
                </th>
                <th className="h-10 px-2 align-middle font-medium text-muted-foreground text-xs text-center">
                  Status
                </th>
                <th className="h-10 w-[11%] align-middle font-medium text-muted-foreground text-xs text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {filteredWorkOrders.map((workOrder) => (
                <tr
                  key={workOrder.id}
                  className="group cursor-pointer hover:bg-muted/30 border-b border-border/40 transition-colors"
                  onClick={() => onViewWorkOrder && onViewWorkOrder(workOrder)}
                >
                  <td className="p-3 align-middle py-3">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-sm-secondary" />
                      <span className="text-sm font-medium text-sm-primary">
                        #{workOrder.id}
                      </span>
                    </div>
                  </td>
                  <td className="p-3 align-middle text-sm">
                    {workOrder.projectName}
                  </td>
                  <td className="p-3 align-middle text-sm text-muted-foreground">
                    {formatDate(workOrder.startDate)}
                  </td>
                  <td className="p-3 align-middle text-sm text-muted-foreground">
                    {formatDate(workOrder.endDate)}
                  </td>
                  <td className="p-3 align-middle">
                    <Badge
                      variant="secondary"
                      className="text-xs font-normal bg-sm-secondary/10 text-sm-secondary border-0"
                    >
                      {workOrder.paymentStructure}
                    </Badge>
                  </td>
                  <td className="p-3 align-middle text-right">
                    <div className="flex flex-col items-end">
                      <span className="text-sm font-medium">
                        {formatCurrency(workOrder.value)}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        Paid: {formatCurrency(workOrder.paidAmount)}
                      </span>
                    </div>
                  </td>
                  <td className="p-3 align-middle text-center">
                    <Badge
                      className={`text-xs font-normal ${getStatusBadgeClass(
                        getDerivedWorkOrderStatus(workOrder),
                      )}`}
                    >
                      {getDerivedWorkOrderStatus(workOrder)}
                    </Badge>
                  </td>
                  <td className="p-3 align-middle text-center">
                    <div className="items-center w-full justify-center gap-1 hidden group-hover:flex transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onViewWorkOrder) onViewWorkOrder(workOrder);
                        }}
                      >
                        <Eye className="h-3.5 w-3.5 text-sm-primary hover:text-white" />
                      </Button>
                      {/* FIXED: now fetches details and opens PDFPreviewModal */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={isLoadingDetails}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadWorkOrder(workOrder);
                        }}
                      >
                        <Download className="h-3.5 w-3.5 text-sm-primary hover:text-white" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}

              {filteredWorkOrders.length === 0 && (
                <tr className="border-b border-border/40">
                  <td
                    colSpan={9}
                    className="p-4 align-middle py-12 text-center"
                  >
                    <div className="flex flex-col items-center">
                      <FileText className="h-10 w-10 text-muted-foreground/50 mb-2" />
                      <p className="text-sm text-muted-foreground">
                        No work orders found
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

    </div>
  );
}
